import { observer } from "mobx-react";
import React, { useEffect } from "react";
import theme from "Theme/AppTheme";
import ConvoWorkflowStore from "./store/ConvoWorkflowStore";
import { useStore } from "utils/useStore";
import { Grid, Tooltip, useMediaQuery } from "@mui/material";
import { WorkflowStatus } from "models/Workflows/WorkflowInstance";
import AcxChip from "components/UI/AcxChip";
import AcxExpansion from "components/UI/AcxExpansion";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import ConvoQuestionAnswerList, {
    buildConvoQuestionVisibilityMap,
    ConvoQuestion,
} from "./questionComponents/ConvoQuestionAnswerList";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { AcxConfirmationDialogWrapper } from "components/UI/AcxConfirmationDialog";
import { truncate } from "lodash";
import ConversationsStore from "components/Conversations/Stores/ConversationsStore";
import AcxButton from "components/UI/AcxButton";
import { LayoutDrawerStore } from "stores/Layout/LayoutDrawerStore";

export const endDialogTitle = "Confirm End Workflow";

const ConvoWorkflowContainer = observer(() => {
    const store = useStore(ConvoWorkflowStore);
    const convoStore = useStore(ConversationsStore);
    const drawerStore = useStore(LayoutDrawerStore);

    const workflowExistsAndIsAssignedToUser =
        !!store.currentWorkflowRes && store.currentWorkflowRes.assignedToUser;

    const isLargeScreen = useMediaQuery("(min-width: 1440px)");

    const isLoading = store.anyTaskLoading || convoStore.anyTaskLoading;

    let workflowLevelActionButtons = <></>;

    useEffect(() => {
        drawerStore.setFooterButtons(workflowLevelActionButtons);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workflowLevelActionButtons, drawerStore.isOpen]);

    return (
        <Grid item>
            <Grid
                container
                item
                xs={12}
                style={{ minHeight: "83vh" }}
                justifyContent={isLoading ? "center" : "flex-start"}
                direction={"column"}
            >
                {isLoading ? (
                    <AcxLoadingIndicator
                        size={24}
                        alternate="PuffLoader"
                        style={{ marginTop: "24px" }}
                    />
                ) : workflowExistsAndIsAssignedToUser ? (
                    store.workflowLevelInfos?.map((wfLevelInfo, i) => {
                        const moduleToUse = store.licensedModules.find(
                            (m) =>
                                m.id ===
                                wfLevelInfo.conversationModule
                                    ?.licensedModuleId,
                        );
                        const convoModuleId =
                            wfLevelInfo.conversationModule?.id;
                        const moduleQuestions = (moduleToUse?.sortedQuestion ?? []).map(
                            (q) => {
                                var convoQuestion = q as ConvoQuestion;
                                convoQuestion.conversationModuleId =
                                    convoModuleId as string;
                                return convoQuestion;
                            },
                        );
                        const visibilityMap = buildConvoQuestionVisibilityMap(
                            moduleQuestions,
                            store.answers,
                        );
                        const sortedQuestions = moduleQuestions.filter(
                            (question) => visibilityMap.get(question.id) ?? false,
                        );

                        // buttons will be up here now
                        let isMissingRequiredAnswer =
                            store.isMissingRequiredAnswer(sortedQuestions);

                        const levelIsEditable =
                            wfLevelInfo.level === store.currentWorkflowLevel &&
                            store.currentWorkflowStatus !==
                                WorkflowStatus.Closed;

                        const completeOrEndButton = (
                            <Grid item>
                                <AcxButton
                                    onClick={() => {
                                        store.dialogStore.setTitle(
                                            endDialogTitle,
                                        );
                                        store.dialogStore.setSubTitle(
                                            "Are you sure you want to end this workflow?",
                                        );
                                        store.dialogStore.isOpen = true;
                                    }}
                                    loading={store.anyTaskLoading}
                                    color={
                                        store.isLastLevel ? "primary" : "error"
                                    }
                                    disabled={isMissingRequiredAnswer}
                                >
                                    {store.isLastLevel
                                        ? "Complete"
                                        : "End Workflow"}
                                </AcxButton>
                            </Grid>
                        );

                        const advanceButton = (
                            <Grid item>
                                <AcxButton
                                    onClick={() => {
                                        store.dialogStore.setTitle(
                                            "Confirm Advance Workflow",
                                        );
                                        store.dialogStore.setSubTitle(
                                            "Are you sure you want to advance this workflow to the next level?",
                                        );
                                        store.dialogStore.isOpen = true;
                                    }}
                                    disabled={isMissingRequiredAnswer}
                                >
                                    Advance
                                </AcxButton>
                            </Grid>
                        );

                        const showCompleteOrEndButton =
                            store.currentWorkflowStatus !==
                                WorkflowStatus.Closed && levelIsEditable;

                        const showAdvanceButton =
                            (store.workflowLevelNames?.length ?? 0) > 1 &&
                            !store.isLastLevel &&
                            levelIsEditable;

                        if (showCompleteOrEndButton || showAdvanceButton) {
                            workflowLevelActionButtons = (
                                <Grid
                                    container
                                    item
                                    xs={12}
                                    spacing={1}
                                    justifyContent={"flex-end"}
                                >
                                    {showCompleteOrEndButton &&
                                        completeOrEndButton}
                                    {showAdvanceButton && advanceButton}
                                </Grid>
                            );
                        }

                        return (
                            <Grid
                                container
                                item
                                xs={12}
                                key={i}
                                style={{
                                    borderColor:
                                        theme.palette.lightgrayBorder.main,
                                    textWrap: "wrap",
                                }}
                            >
                                <AcxExpansion
                                    header={
                                        <Grid
                                            item
                                            container
                                            alignItems={"center"}
                                            wrap="nowrap"
                                        >
                                            <AcxChip
                                                size="small"
                                                label={
                                                    <Grid
                                                        item
                                                        container
                                                        justifyContent={
                                                            "center"
                                                        }
                                                        alignItems={"center"}
                                                        wrap="nowrap"
                                                    >
                                                        <ArrowRightAltIcon />

                                                        <Grid item>
                                                            {"Level " +
                                                                (wfLevelInfo.level +
                                                                    1)}
                                                        </Grid>
                                                    </Grid>
                                                }
                                                color="blue"
                                                style={{
                                                    width: "85px",
                                                    marginRight:
                                                        theme.spacing(1),
                                                    borderRadius: "28px",
                                                }}
                                            />
                                            <Tooltip
                                                title={wfLevelInfo.levelName}
                                            >
                                                <Grid item>
                                                    {truncate(
                                                        wfLevelInfo.levelName,
                                                        {
                                                            length: isLargeScreen
                                                                ? 40
                                                                : 20,
                                                        },
                                                    )}
                                                </Grid>
                                            </Tooltip>
                                        </Grid>
                                    }
                                    body={
                                        <ConvoQuestionAnswerList
                                            convoModuleId={convoModuleId}
                                            isEditable={
                                                wfLevelInfo.level ===
                                                    store.currentWorkflowLevel &&
                                                store.currentWorkflowStatus !==
                                                    WorkflowStatus.Closed
                                            }
                                            workflowLevel={wfLevelInfo.level}
                                            uiModel={store.uiModels?.find(
                                                (m) =>
                                                    m.conversationModuleId ===
                                                    wfLevelInfo
                                                        .conversationModule?.id,
                                            )}
                                            sortedQuestions={
                                                sortedQuestions as ConvoQuestion[]
                                            }
                                            isQuestionAutofilled={() => false}
                                            setAnswerForQuestion={
                                                store.setAnswerForQuestion
                                            }
                                            getAnswerForQuestion={
                                                store.getAnswerForQuestion
                                            }
                                            setFillInAnswerForQuestion={
                                                store.setFillInAnswerForQuestion
                                            }
                                        />
                                    }
                                    rootStyle={{
                                        flexWrap: "nowrap",
                                        maxWidth: "100%",
                                    }}
                                    headerSx={{ height: "50px" }}
                                    expanded={
                                        store.currentWorkflowLevel ===
                                        wfLevelInfo.level
                                    }
                                />
                            </Grid>
                        );
                    })
                ) : (
                    <>
                        Unable to find workflow for this conversation assigned
                        to this user.
                    </>
                )}
            </Grid>

            <AcxConfirmationDialogWrapper
                dialogStore={store.dialogStore}
                title={store.dialogStore.title}
                content={store.dialogStore.subTitle}
                onConfirm={() => {
                    store.advanceOrCloseClassifierWorkflow(
                        !!store.dialogStore.title?.includes(endDialogTitle),
                    );
                    store.dialogStore.isOpen = false;
                }}
                onCancel={() => (store.dialogStore.isOpen = false)}
            />
        </Grid>
    );
});

export default ConvoWorkflowContainer;
